import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, CalendarRange, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { addDays, fromISO, toISO, todayISO, useOccurrences } from "@/lib/calendar";
import { OccChip, useCalendarDialogs } from "@/components/calendar/parts";

const ROW = 44;
const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const span = (o) => { const s = toMin(o.time); return [s, o.end_time ? Math.max(toMin(o.end_time), s + 30) : s + 45]; };

// Side-by-side columns for overlapping events, like Google Calendar.
const layout = (items) => {
  const out = [];
  let group = [], groupEnd = -1;
  const flush = () => { const cols = Math.max(...group.map((g) => g.col)) + 1; group.forEach((g) => out.push({ ...g, cols })); group = []; };
  items.forEach((o) => {
    const [s, e] = span(o);
    if (group.length && s >= groupEnd) flush();
    const used = new Set(group.filter((g) => g.e > s).map((g) => g.col));
    let col = 0; while (used.has(col)) col += 1;
    group.push({ o, s, e, col }); groupEnd = Math.max(groupEnd, e);
  });
  if (group.length) flush();
  return out;
};

const Timeline = ({ items, isToday, onOpen }) => {
  const ref = useRef(null);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  useEffect(() => {
    if (!ref.current) return;
    const first = items[0]?.time ? toMin(items[0].time) : 8 * 60;
    const focus = isToday ? Math.min(nowMin, first) : first;
    ref.current.scrollTop = Math.max(0, (focus / 60 - 1) * ROW);
  }, [items, isToday]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={ref} className="relative h-64 overflow-y-auto overscroll-contain" data-testid="cal-day-timeline">
      <div className="relative" style={{ height: 24 * ROW }}>
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="absolute left-0 right-0 flex items-start" style={{ top: h * ROW }}>
            <span className="w-11 -mt-2 text-[10.5px] text-slate-400 tabular-nums text-right pr-2">{h ? `${String(h).padStart(2, "0")}:00` : ""}</span>
            <span className="flex-1 border-t border-slate-100" />
          </div>
        ))}
        {layout(items).map(({ o, s, e, col, cols }, i) => (
          <div key={`${o.item_id}-${i}`} className="absolute px-px"
            style={{ top: (s / 60) * ROW + 1, height: ((e - s) / 60) * ROW - 2, left: `calc(3rem + (100% - 3.25rem) * ${col / cols})`, width: `calc((100% - 3.25rem) / ${cols})` }}>
            <OccChip o={o} onClick={onOpen} fill />
          </div>
        ))}
        {isToday && (
          <div className="absolute left-10 right-0 flex items-center pointer-events-none" style={{ top: (nowMin / 60) * ROW }} data-testid="cal-now-line">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-900 -ml-1" /><span className="flex-1 h-px bg-slate-900" />
          </div>
        )}
      </div>
    </div>
  );
};

export default function CalendarDayCard() {
  const [day, setDay] = useState(todayISO());
  const [overdue, setOverdue] = useState(0);
  const { items, reload } = useOccurrences(day, day);
  const loadOverdue = () => api.get("/calendar/overdue", { silent: true }).then((r) => setOverdue(r.data.length)).catch(() => {});
  useEffect(() => { loadOverdue(); }, []);
  const refresh = () => { reload(); loadOverdue(); };
  const { openOcc, openNew, dialogs } = useCalendarDialogs(refresh);
  const d = fromISO(day);
  const isToday = day === todayISO();
  const allDay = items.filter((o) => !o.time);
  const timed = items.filter((o) => o.time);
  const shift = (n) => setDay(toISO(addDays(d, n)));

  return (
    <section className="bg-white rounded-2xl border border-slate-200 card-shadow overflow-hidden" data-testid="cal-day-card">
      <div className="flex items-center gap-1 px-3 pt-3 pb-2">
        <h3 className="font-heading text-sm font-black text-[#1e3a8a] flex-1">Calendário</h3>
        <button onClick={() => shift(-1)} className="p-1.5 rounded-full hover:bg-slate-100" aria-label="Dia anterior" data-testid="cal-day-prev"><ChevronLeft className="w-4 h-4" /></button>
        {!isToday && <button onClick={() => setDay(todayISO())} className="text-[11px] font-bold px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50" data-testid="cal-day-today">Hoje</button>}
        <button onClick={() => shift(1)} className="p-1.5 rounded-full hover:bg-slate-100" aria-label="Próximo dia" data-testid="cal-day-next"><ChevronRight className="w-4 h-4" /></button>
        <Link to="/calendario" className="ml-1 inline-flex items-center gap-1 text-[11px] font-bold text-[#1a73e8] px-2 py-1 rounded-full hover:bg-blue-50" data-testid="cal-open-full"><CalendarRange className="w-3.5 h-3.5" />Abrir</Link>
        <button onClick={() => openNew(day)} className="ml-1 w-8 h-8 rounded-xl bg-[#d3e3fd] text-[#1a73e8] flex items-center justify-center hover:brightness-95 active:scale-95 transition-[filter,transform]" aria-label="Novo agendamento" data-testid="cal-day-add"><Plus className="w-5 h-5" /></button>
      </div>
      {overdue > 0 && (
        <Link to="/calendario?atrasados=1" className="mx-3 mb-2 flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12px] font-bold bg-[#fef7e0] text-[#7a5900]" data-testid="cal-overdue-banner">
          <AlertTriangle className="w-4 h-4" />{overdue} atrasado(s) — finalize ou reagende<ChevronRight className="w-4 h-4 ml-auto" />
        </Link>
      )}
      <div className="flex gap-2 px-3 pb-2 border-b border-slate-100 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.08)]">
        <div className="w-11 text-center shrink-0">
          <p className={`text-[10px] font-bold uppercase ${isToday ? "text-[#1a73e8]" : "text-slate-500"}`}>{d.toLocaleDateString("pt-BR", { weekday: "short" })}</p>
          <p className={`mx-auto w-9 h-9 rounded-full flex items-center justify-center text-lg ${isToday ? "bg-[#1a73e8] text-white" : "text-slate-800"}`} data-testid="cal-day-number">{d.getDate()}</p>
        </div>
        <div className="flex-1 min-w-0 space-y-1 pt-1" data-testid="cal-day-allday">
          {allDay.map((o) => <OccChip key={o.item_id} o={o} onClick={openOcc} />)}
          {!items.length && <p className="text-[12px] text-slate-400 pt-2">Nada agendado neste dia.</p>}
        </div>
      </div>
      <Timeline items={timed} isToday={isToday} onOpen={openOcc} />
      {dialogs}
    </section>
  );
}
